// src/features/estoque/components/ModalCertificadoSefaz.jsx
import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Checkbox } from "components/ui/checkbox";
import {
  ShieldCheck,
  Upload,
  Key,
  Eye,
  EyeOff,
  FileCheck,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

export const ModalCertificadoSefaz = ({
  isOpen,
  onClose,
  onConfirmar,
  isProcessing = false,
  notasCount = 1,
}) => {
  const [arquivo, setArquivo] = useState(null);
  const [pfxBase64, setPfxBase64] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [lembrarSessao, setLembrarSessao] = useState(true);
  const fileInputRef = useRef(null);

  const handleArquivoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase();
    if (!ext.endsWith(".pfx") && !ext.endsWith(".p12")) {
      toast.error("Por favor, selecione um arquivo de certificado A1 (.pfx ou .p12)");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      // Remove prefixo data:*/*;base64,
      const base64 = dataUrl.split(",")[1];
      setPfxBase64(base64);
      setArquivo(file);
      toast.success(`Certificado "${file.name}" carregado!`, { icon: "🔐" });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmeter = (e) => {
    e?.preventDefault();
    if (!pfxBase64) {
      toast.error("Selecione o arquivo do Certificado Digital A1 (.pfx)");
      return;
    }
    if (!senha) {
      toast.error("Informe a senha do Certificado Digital");
      return;
    }

    onConfirmar({
      pfxBase64,
      senha,
      nomeArquivo: arquivo?.name || "certificado.pfx",
      lembrarSessao,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isProcessing && !open && onClose()}>
      <DialogContent className="max-w-md w-[95vw] sm:w-full p-5 sm:p-6 rounded-2xl border-slate-200 dark:border-slate-800 shadow-2xl">
        <DialogHeader className="space-y-2 text-left">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <span>Certificado Digital A1</span>
                <span className="text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                  SEFAZ
                </span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
                Necessário para manifestar e baixar {notasCount > 1 ? `${notasCount} notas fiscais` : "a nota fiscal"} na SEFAZ.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Informação de Privacidade e Segurança */}
        <div className="bg-slate-50 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 rounded-xl p-3 text-xs text-slate-600 dark:text-slate-300 flex items-start gap-2.5 shadow-sm">
          <Lock className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            <strong className="text-slate-900 dark:text-slate-100">Privacidade Absoluta:</strong> Seu certificado e senha <strong>nunca</strong> são gravados em disco nem no banco de dados. Ficam apenas na memória temporária da transmissão.
          </p>
        </div>

        <form onSubmit={handleSubmeter} className="space-y-4 pt-1">
          {/* Campo 1: Upload do Arquivo .PFX */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Arquivo do Certificado (.pfx ou .p12)
            </Label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleArquivoChange}
              accept=".pfx,.p12"
              className="hidden"
            />
            {arquivo ? (
              <div className="flex items-center justify-between p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 dark:bg-emerald-950/20">
                <div className="flex items-center gap-2.5 min-w-0">
                  <FileCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100 truncate">
                      {arquivo.name}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {(arquivo.size / 1024).toFixed(1)} KB • Pronto para transmissão
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs h-7 px-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  Trocar
                </Button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-200 dark:border-slate-800 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 rounded-xl p-4 text-center transition-colors flex flex-col items-center justify-center gap-1.5 group cursor-pointer bg-slate-50/50 dark:bg-slate-950/30"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 group-hover:text-emerald-600 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/50 transition-colors">
                  <Upload className="w-4 h-4" />
                </div>
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                  Toque para selecionar o arquivo .pfx
                </span>
                <span className="text-[10px] text-slate-400">
                  Compatível com celular e computador
                </span>
              </button>
            )}
          </div>

          {/* Campo 2: Senha do Certificado */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Senha do Certificado Digital
            </Label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Key className="w-4 h-4" />
              </div>
              <Input
                type={mostrarSenha ? "text" : "password"}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="Digite a senha do certificado..."
                className="pl-9 pr-10 text-xs h-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800"
                autoFocus={!!arquivo}
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                tabIndex={-1}
              >
                {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Checkbox: Lembrar nesta sessão */}
          <div className="flex items-center space-x-2 pt-0.5">
            <Checkbox
              id="lembrarSessao"
              checked={lembrarSessao}
              onCheckedChange={(checked) => setLembrarSessao(!!checked)}
            />
            <label
              htmlFor="lembrarSessao"
              className="text-xs text-slate-600 dark:text-slate-400 leading-tight cursor-pointer select-none"
            >
              Manter ativo nesta aba (não pedir novamente até fechar)
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
              className="text-xs h-9 border-slate-200 dark:border-slate-700"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!pfxBase64 || !senha || isProcessing}
              className="text-xs h-9 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 gap-1.5 shadow-sm"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Conectando SEFAZ...
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  Baixar na SEFAZ ({notasCount})
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
